import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Heading,
  Link,
  ListItem,
  OrderedList,
  Text,
  VStack,
} from "@chakra-ui/react";

interface PushNotificationTermsAndConditionsProps {
  businessId: string;
  onAccept: () => Promise<void>;
}

export const PushNotificationTermsAndConditions: React.FC<PushNotificationTermsAndConditionsProps> =
  ({ businessId, onAccept }) => {
    const [isAccepted, setIsAccepted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleAccept = async () => {
      setIsLoading(true);
      try {
        await onAccept();
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <Box maxW="800px" mx="auto" p={6}>
        <VStack align="stretch" spacing={6}>
          <Heading size="md">Push Notifications Terms & Conditions</Heading>

          <Text>
            Before enabling Push Notifications in Cherryhub, you must agree to
            Google and Apple's terms governing the use of push notifications.
          </Text>

          <Text fontWeight="bold">By proceeding, you acknowledge that:</Text>

          <OrderedList spacing={3} pl={4}>
            <ListItem>
              Push Notifications must comply with the{" "}
              <Link
                href="https://developer.apple.com/support/terms/apple-developer-program-license-agreement/#A1"
                color="blue.500"
                isExternal
              >
                Apple Push Notification Terms & Conditions
              </Link>{" "}
              and{" "}
              <Link
                href="https://developers.google.com/wallet/generic/use-cases/trigger-push-notifications#some-considerations-when-sending-messages-with-notifications-to-users"
                color="blue.500"
                isExternal
              >
                Google Push Notification Terms & Conditions
              </Link>
              .
            </ListItem>
            <ListItem>
              Notifications must only be used for{" "}
              <Text as="span" fontWeight="bold">
                relevant, timely, and user-requested content
              </Text>{" "}
              directly related to your business's digital passes (e.g.,
              membership updates, rewards, or check-ins).
            </ListItem>
            <ListItem>
              Notifications{" "}
              <Text as="span" fontWeight="bold">
                must not be used for spam, mass marketing, or unauthorized
                promotional advertising
              </Text>
              .
            </ListItem>
            <ListItem>
              You are responsible for ensuring your use of Push Notifications
              complies with{" "}
              <Text as="span" fontWeight="bold">
                Google and Apple's App Store Review Guidelines, privacy laws,
                and user consent requirements
              </Text>
              .
            </ListItem>
            <ListItem>
              Messages may contain URIs to your website or app.{" "}
              <Text as="span" fontWeight="bold">
                Hyperlinks must be a website or app related to the pass.
              </Text>{" "}
              It is a violation of the{" "}
              <Link
                href="https://payments.developers.google.com/terms/aup"
                color="blue.500"
                isExternal
              >
                Acceptable Use Policy
              </Link>{" "}
              to send users to links not related to the pass.
            </ListItem>
            <ListItem>
              Cherryhub may revoke access to the Push Notifications feature if
              these conditions are breached.
            </ListItem>
          </OrderedList>

          <Checkbox
            isChecked={isAccepted}
            onChange={(e) => setIsAccepted(e.target.checked)}
          >
            <Text fontSize="sm">
              I have read and agree to the Cherryhub Push Notification
              Guidelines and confirm that notifications sent will relate only to
              digital pass–specific content, not for promotional or unrelated
              use.
            </Text>
          </Checkbox>

          <Button
            colorScheme="red"
            isDisabled={!isAccepted}
            isLoading={isLoading}
            onClick={handleAccept}
          >
            Agree & Continue
          </Button>
        </VStack>
      </Box>
    );
  };
